/**
 * Drawer tap guard (iOS first-tap fix).
 *
 * The theme's dialog elements (header-sidebar, cart-drawer, x-drawer, x-modal,
 * x-popover, pop-in, cart-note-dialog...) all extend the same DialogElement base in
 * theme.js. That base sets/removes the `open` attribute and dispatches
 * `dialog:before-show`, `dialog:after-show`, `dialog:before-hide`, `dialog:after-hide`
 * on the element (non-bubbling CustomEvents, so we listen in the capture phase).
 *
 * While a dialog is animating in, its overlay must not accept a close tap: on iOS
 * Safari the tap lands on an element whose transform/opacity is still changing and
 * is swallowed as hover. While a dialog is animating out, no click may reach the
 * page underneath, otherwise the closing tap ghost-clicks a product card.
 *
 * This file is deliberately generic: it keys off the `open` attribute and the
 * `dialog:*` events only. It does not touch animation durations or the focus trap.
 */
(function () {
  'use strict';

  if (window.__drawerTapGuard) return;
  window.__drawerTapGuard = true;

  var STALE_MS = 2000; // safety net: never keep blocking if an after-* event never fires
  var transitioning = new Map(); // element -> { state: 'showing' | 'hiding', at: timestamp }

  function isDialog(node) {
    return node && node.nodeType === 1 && typeof node.hasAttribute === 'function';
  }

  function mark(state) {
    return function (event) {
      var el = event.target;
      if (!isDialog(el)) return;
      transitioning.set(el, { state: state, at: Date.now() });
    };
  }

  function unmark(event) {
    var el = event.target;
    if (isDialog(el)) transitioning.delete(el);
  }

  document.addEventListener('dialog:before-show', mark('showing'), true);
  document.addEventListener('dialog:after-show', unmark, true);
  document.addEventListener('dialog:before-hide', mark('hiding'), true);
  document.addEventListener('dialog:after-hide', unmark, true);

  function overlayOf(el) {
    if (!el.shadowRoot) return null;
    return el.shadowRoot.querySelector('[part~="overlay"]');
  }

  function swallow(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function guard(event) {
    if (transitioning.size === 0) return;

    var now = Date.now();
    var path = typeof event.composedPath === 'function' ? event.composedPath() : [];

    transitioning.forEach(function (info, el) {
      if (now - info.at > STALE_MS || !el.isConnected) {
        transitioning.delete(el);
        return;
      }

      // Opening: the `open` attribute is set and `dialog:after-show` has not fired yet.
      // A tap on this dialog's overlay must not close it.
      if (info.state === 'showing' && el.hasAttribute('open')) {
        var overlay = overlayOf(el);
        if (overlay && path.indexOf(overlay) !== -1) {
          swallow(event);
        }
        return;
      }

      // Closing: between `dialog:before-hide` and `dialog:after-hide`.
      // Block clicks on anything outside the dialog so the closing tap cannot reach
      // the product card underneath. Clicks inside the dialog are left alone.
      if (info.state === 'hiding' && event.type === 'click') {
        if (path.indexOf(el) === -1) {
          swallow(event);
        }
      }
    });
  }

  /*
   * Menu item stagger (touch devices only).
   * After the sidebar has slid in, theme.js `revealItems()` fades the menu items in one
   * after another (0.1 s stagger per item), and opening a sub-menu does the same for its
   * items. On the rings collection that keeps elements animating for ~1.5 s after the
   * drawer is visibly open, and iOS Safari drops every tap made in that window (the close
   * button needed 3 taps in the iPhone trace). On devices without hover we jump those
   * item animations to their end state as soon as they start. The drawer slide itself
   * (on the element / its shadow parts) is untouched, and desktop keeps the full effect.
   */
  var noHover = window.matchMedia && window.matchMedia('(hover: none)').matches;
  var ITEM_SELECTOR = [
    '.header-sidebar__main-panel',
    '.header-sidebar__sub-panel',
    '.header-sidebar__linklist li',
    '.header-sidebar__back-button',
    '.header-sidebar__footer',
    '.header-sidebar__promo'
  ].join(',');

  function finishItemAnimations(sidebar) {
    if (typeof Element.prototype.getAnimations !== 'function') return;
    sidebar.querySelectorAll(ITEM_SELECTOR).forEach(function (node) {
      node.getAnimations().forEach(function (animation) {
        try { animation.finish(); } catch (e) { /* infinite or detached animation: leave it */ }
      });
    });
  }

  // The theme starts the stagger from its own listener, which runs after ours, and
  // sub-menus start theirs after a 0.15 s fade. Sweep for a short while so we catch it.
  function sweep(sidebar, ms) {
    var until = performance.now() + ms;
    (function tick() {
      finishItemAnimations(sidebar);
      if (performance.now() < until && sidebar.hasAttribute('open')) requestAnimationFrame(tick);
    })();
  }

  if (noHover) {
    document.addEventListener('dialog:after-show', function (event) {
      var el = event.target;
      if (el && el.tagName === 'HEADER-SIDEBAR') sweep(el, 400);
    }, true);

    document.addEventListener('click', function (event) {
      var path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      for (var i = 0; i < path.length; i++) {
        if (path[i].tagName === 'HEADER-SIDEBAR') {
          if (path[i].hasAttribute('open')) sweep(path[i], 700);
          return;
        }
      }
    }, true);
  }

  /*
   * Quick Add + Section Store cart.
   * The theme closes an open quick-buy-modal when its own product form fires `variant:add`.
   * The Section Store cart app takes over add-to-cart, so that event never fires: the quick
   * add popup stays open (and the page stays locked) underneath the app's cart drawer. Its
   * focus trap then cancels the first tap on anything outside it, i.e. on the cart, which
   * makes the cart look frozen on iPhone. Close any open theme modal as soon as the app says
   * its cart opened or an item was added.
   */
  function closeQuickBuyModals() {
    document.querySelectorAll('quick-buy-modal[open]').forEach(function (modal) {
      if (typeof modal.hide === 'function') modal.hide();
    });
  }
  ['section-store:cart:opened', 'section-store:cart:item-added'].forEach(function (type) {
    window.addEventListener(type, closeQuickBuyModals);
  });

  ['pointerdown', 'touchstart', 'mousedown', 'click'].forEach(function (type) {
    // touchstart must stay passive:false to be cancelable; capture so we run before
    // the theme's own overlay/focus-trap listeners.
    document.addEventListener(type, guard, { capture: true, passive: false });
  });
})();
